//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext } from 'react';
import { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
// import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';
import AfitdButtonGroup from '../../common/elemental/AfitdButtonGroup';
import AfitdButton from '../../common/elemental/AfitdButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import { AppContext, LayoutContext } from '../../common/contexts';
import { useHistory } from 'react-router-dom';
import { BASE_URL } from '../../common/constants';
import { PATH_DASHBOARD } from '../../system/constants';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: { padding: '0px 15px 15px 15px' },
        companyLogo: { marginRight: 16, borderRadius: '50%' },
        inviterProfilePic: { marginRight: 16 },
        content: { display: 'flex', alignItems: 'center', padding: '15px 0px' },
        buttonsContainer: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', background: 'rgba(0, 0, 0, 0.04)', borderRadius: 5, padding: 12 },
        // declinedButton: { fontSize: 14 },
        // viewDashboardButton: { fontSize: 14 },
        inviterYouText: { color: 'rgba(17, 30, 20, 0.4)' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, props, isLinkageCompleted, isAccept, handleClickAcceptButton, handleClickDeclineButton, handleClickViewDashboardButton } = params;
    const { data } = props;

    const companyProfilePicUrl = _get(data, 'companyProfilePicUrl');
    const companyName = _get(data, 'companyName');
    const inviterProfilePicUrl = _get(data, 'inviterProfilePicUrl');
    const inviterName = _get(data, 'inviterName');

    const defns = {
        companyLogo: {
            variant: 'rounded',
            src: companyProfilePicUrl,
        },
        companyName: { variant: 'body1', value: companyName },
        inviterProfilePic: {
            src: inviterProfilePicUrl,
        },
        inviterYouText: { variant: 'body1', component: 'span', value: ' (invited you)' },
        inviterName: { variant: 'body1', value: inviterName },
        acceptDeclineButtonGroup: { isHidden: isLinkageCompleted },
        acceptButton: { color: isAccept === true ? 'secondary' : '', onClick: handleClickAcceptButton },
        declineButton: { color: isAccept === false ? 'secondary' : '', onClick: handleClickDeclineButton },
        declinedButton: { disabled: true, isHidden: !isLinkageCompleted || isAccept === true, classes: { label: { fontSize: 14 } } },
        viewDashboardButton: {
            color: 'secondary',
            isHidden: !isLinkageCompleted || isAccept === false,
            onClick: handleClickViewDashboardButton,
            classes: { label: { fontSize: 14 } },
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const EmployerInviteItem = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    const appContext = useContext(AppContext);
    const { setCurrentEmployerIdx } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { isLinkageCompleted /*, setSnackbarMessage, setSnackbarErrorMessage */ } = layoutContext;

    const history = useHistory();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { idx, onAccept, onDecline } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [isDirty, setIsDirty] = useState(false);
    const [isAccept, setIsAccept] = useState();

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickAcceptButton = () => {
        setIsAccept(true);
        if (onAccept) onAccept();
    };

    const handleClickDeclineButton = () => {
        setIsAccept(false);
        if (onDecline) onDecline();
    };

    const handleClickViewDashboardButton = () => {
        setCurrentEmployerIdx(idx);
        history.push(`${BASE_URL}${PATH_DASHBOARD}`);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, isLinkageCompleted, isAccept, handleClickAcceptButton, handleClickDeclineButton, handleClickViewDashboardButton });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div>
                <div {...defns.content}>
                    <AfitdAvatar {...defns.companyLogo} />
                    <AfitdTypography {...defns.companyName} />
                </div>
                <div>
                    <Divider />
                </div>
                <div {...defns.content}>
                    <AfitdAvatar {...defns.inviterProfilePic} />
                    <AfitdTypography {...defns.inviterName} />
                    <span>&nbsp;</span>
                    <AfitdTypography {...defns.inviterYouText} />
                </div>
            </div>
            <div {...defns.buttonsContainer}>
                <AfitdButtonGroup variant="contained" aria-label="contained button group" {...defns.acceptDeclineButtonGroup}>
                    <AfitdButton isGroup={true} {...defns.acceptButton}>
                        Accept
                    </AfitdButton>
                    <AfitdButton isGroup={true} {...defns.declineButton}>
                        Decline
                    </AfitdButton>
                </AfitdButtonGroup>
                <AfitdButton {...defns.declinedButton}>Declined</AfitdButton>
                <AfitdButton {...defns.viewDashboardButton}>View Dashboard</AfitdButton>
            </div>
        </div>
    );
};

export default EmployerInviteItem;
